import { Selection } from '@/modules/user/types'
import { t } from '@/plugins/i18n'
import { InternalItem, Language } from '@/types'
import { Department, PaymentMethod, ShortUser, User, ContactTitle } from '@/types/index'

const prepareForForm = (user: User): User => {
  user.phoneMobile = user.phoneMobile || ''
  user.phone = user.phone || ''

  return user
}

const getContactTitles = (): Selection[] => {
  const titles: Selection[] = []
  Object.values(ContactTitle)
    .filter((x) => isNaN(parseInt(x)))
    .forEach((title, index) => {
      titles.push({
        value: index,
        title: title,
        translation: t('contactTitles.' + title.toLowerCase()).toString()
      })
    })
  return titles
}

const getEmailDomain = (mail: string) => {
  return mail.substring(mail.indexOf('@') + 1)
}

const getDepartmentTitles = (): Selection[] => {
  const titles: Selection[] = []
  Object.values(Department)
    .filter((x) => isNaN(parseInt(x)))
    .forEach((department, index) => {
      if (department != Department.REGISTRATION_PENDING) {
        titles.push({
          value: index,
          title: department,
          translation: t('departments.' + department).toString()
        })
      }
    })

  return titles.sort((a, b) => {
    if (a.translation > b.translation) {
      return 1
    } else {
      return b.translation > a.translation ? -1 : 0
    }
  })
}

const getSortedDepartments = (): Selection[] => {
  const sortedDepartements = getDepartmentTitles()
  sortedDepartements.push({
    value: sortedDepartements.length,
    title: Department.REGISTRATION_PENDING,
    translation: t('departments.' + Department.REGISTRATION_PENDING).toString()
  })
  return sortedDepartements
}

const getLanguages = (): Selection[] => {
  const languages: Selection[] = []
  Object.keys(Language)
    .filter((x) => isNaN(parseInt(x)))
    .forEach((lang, index) => {
      languages.push({
        value: index,
        title: lang,
        translation: t('languages.' + lang).toString()
      })
    })
  return languages
}

const getMobexPaymentMethods = (): Selection[] => {
  const paymentMethods: Selection[] = []
  Object.keys(PaymentMethod)
    .filter((x) => isNaN(parseInt(x)))
    .forEach((method, index) => {
      paymentMethods.push({
        value: index,
        title: method,
        translation: t('mobexPaymentMethod.' + method).toString()
      })
    })
  return paymentMethods
}

const customShortUserFilter = (_: string, query: string, item?: InternalItem<ShortUser>) => {
  return (
    item?.raw.firstName?.toLowerCase().includes(query) ||
    item?.raw.lastName?.toLowerCase().includes(query) ||
    false
  )
}

// nameAndEmailFilter and sortByName are not exclusive for users but also for user like data types like orderLimitUsers
const nameAndEmailFilter =
  <T extends { firstName?: string | null; lastName?: string | null; email?: string | null }>(
    query: string
  ) =>
  (a: T): boolean => {
    const q = query.toLowerCase()
    return (
      (a.lastName ? a.lastName.toLowerCase().includes(q) : false) ||
      (a.firstName ? a.firstName.toLowerCase().includes(q) : false) ||
      (a.email ? a.email?.toLowerCase().includes(q) : false)
    )
  }

const sortByName = <
  T extends { firstName?: string | null; lastName?: string | null; email?: string | null }
>(
  a: T,
  b: T
) => {
  if ((a.lastName || a.email || '') > (b.lastName || b.email || '')) {
    return 1
  } else if ((b.lastName || b.email || '') > (a.lastName || a.email || '')) {
    return -1
  } else if ((a.firstName || '') > (b.firstName || '')) {
    return 1
  } else if ((b.firstName || '') > (a.firstName || '')) {
    return -1
  } else {
    return 0
  }
}

export {
  customShortUserFilter,
  getContactTitles,
  getDepartmentTitles,
  getEmailDomain,
  getLanguages,
  getMobexPaymentMethods,
  getSortedDepartments,
  nameAndEmailFilter,
  prepareForForm,
  sortByName
}
